/**
 * "Include management" — an on/off switch at the top of each page.
 *
 * It lived in the filter bar's scope row as a three-way select (included / excluded /
 * management only). Moved into the page and made a switch on 2026-09-24: whether the
 * figures count supervisors and managers is part of reading the page, not one filter
 * among fifteen, and it only ever has two useful answers.
 *
 * Each tab opens on its own default (Shayna, 2026-09-23). The Overview is the whole
 * book, so it counts everyone. The personal and people-ranking tabs leave management
 * out: a supervisor covering a few claims should not read as a $16,000 claim, nor pull
 * their own team's rate. "Management" is the payroll flag, so an Account Manager is not
 * caught by the word in their title. Locking the switch for everyone but executives
 * waits on per-user roles, which the app does not have yet.
 */
import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { isStamped, useFilters, type Filters } from '@/data/filters'
import { liveState } from '@/ui/urlState'
import { define } from '@/measures/definitions'
import { Info } from './Info'

export const MANAGEMENT_DEFAULT: Record<string, Filters['management']> = {
  '/employee-view': 'no',
  '/supervisors': 'no',
  '/scorecard': 'no',
}

export const managementDefaultFor = (path: string): Filters['management'] =>
  MANAGEMENT_DEFAULT[path] ?? 'all'

export function ManagementToggle() {
  const { filters, set } = useFilters()
  const { pathname } = useLocation()

  // A new tab puts back that tab's default. An entry that already says what its
  // filters are — Back or Forward to it, a reload, a shared link — keeps its own.
  useEffect(() => {
    if (isStamped({ search: window.location.search, state: liveState() })) return
    set('management', managementDefaultFor(pathname))
    // `set` is rebuilt on every filter change; only a new tab should reapply this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  // "Management only" was the select's third answer; a switch reads it as included.
  const on = filters.management !== 'no'

  return (
    <div className="mgmt-toggle">
      <button
        type="button"
        role="switch"
        aria-checked={on}
        className="switch"
        onClick={() => set('management', on ? 'no' : 'all')}
      >
        <span className="switch-track" aria-hidden="true">
          <span className="switch-thumb" />
        </span>
        <span className="switch-label">Include management</span>
      </button>
      <span className="mgmt-toggle-note">
        {on ? 'Supervisors, managers and above are counted' : 'Supervisors, managers and above are left out'}
      </span>
      <Info def={define('management')} />
    </div>
  )
}
